import React, { useState } from "react";
import ChatPanel from "../components/ChatPanel";
import { MessagesSquare, Trash2, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [confirming, setConfirming] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [panelKey, setPanelKey] = useState(0);

  const clearChat = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete("/chat/global");
      toast.success(`Team chat cleared — ${data.deleted} message${data.deleted === 1 ? "" : "s"} removed`);
      setConfirming(false);
      setPanelKey((k) => k + 1);
    } catch {
      toast.error("Could not clear chat history");
    }
    setClearing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-700 tracking-tight text-slate-100 flex items-center gap-2">
            <MessagesSquare className="h-7 w-7 text-teal" /> Team Chat
          </h1>
          <p className="text-sm text-slate-400 mt-1">One room for the whole team — share updates, ask clarifications, and send documents.</p>
        </div>
        {isAdmin && (
          <button
            data-testid="clear-chat-btn"
            onClick={() => setConfirming(true)}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-red-500/30 px-3 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/15 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" /> Clear chat history
          </button>
        )}
      </div>

      <div className="max-w-5xl">
        <ChatPanel key={panelKey} scope="global" height="h-[calc(100vh-230px)]" />
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirming(false)}>
          <div data-testid="clear-chat-modal" className="w-full max-w-sm rounded-lg bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-600 flex items-center gap-2 text-red-300"><Trash2 className="h-4 w-4" /> Clear chat history?</h3>
            <p className="text-sm text-slate-400 mt-2">This permanently deletes <span className="font-semibold text-slate-200">all</span> Team Chat messages for everyone. This cannot be undone.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="clear-chat-cancel" onClick={() => setConfirming(false)}
                className="rounded-md border border-white/15 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-white/5 transition-colors">Cancel</button>
              <button data-testid="clear-chat-confirm" onClick={clearChat} disabled={clearing}
                className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-60">
                {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete all messages
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
